import { api, ApiError, unwrap } from '@/core/http';
import type { MaterialTicket } from '../domain/materials';

export interface ListCoursesParams {
  includeDrafts?: boolean;
  tag?: string;
  limit?: number;
  offset?: number;
}

type CreateCourseBody = { title: string; description: string; slug: string | null; tags: string[] };
/** Parcial: só o que muda. `author_name: null` apaga. */
type UpdateCourseBody = {
  title?: string;
  description?: string;
  tags?: string[];
  author_name?: string | null;
};
type UpdateLessonBody = { title?: string; description?: string };
type AddLessonBody = {
  title: string;
  description: string;
  duration_s: number;
  kind: string;
  required: boolean;
};

const course = (course_id: string) => ({ params: { path: { course_id } } });

export const catalogApi = {
  list: ({ includeDrafts = true, tag, limit = 200, offset = 0 }: ListCoursesParams = {}) =>
    unwrap(
      api.GET('/v1/catalog/courses', {
        params: { query: { include_drafts: includeDrafts, tag, limit, offset } },
      }),
    ),

  byId: (courseId: string) => unwrap(api.GET('/v1/catalog/courses/{course_id}', course(courseId))),

  bySlug: (slug: string) =>
    unwrap(api.GET('/v1/catalog/courses/by-slug/{slug}', { params: { path: { slug } } })),

  create: (body: CreateCourseBody) => unwrap(api.POST('/v1/catalog/courses', { body })),

  update: (courseId: string, body: UpdateCourseBody) =>
    unwrap(api.PATCH('/v1/catalog/courses/{course_id}', { ...course(courseId), body })),

  renameModule: (courseId: string, moduleId: string, title: string) =>
    unwrap(
      api.PATCH('/v1/catalog/courses/{course_id}/modules/{module_id}', {
        params: { path: { course_id: courseId, module_id: moduleId } },
        body: { title },
      }),
    ),

  deleteModule: (courseId: string, moduleId: string) =>
    unwrap(
      api.DELETE('/v1/catalog/courses/{course_id}/modules/{module_id}', {
        params: { path: { course_id: courseId, module_id: moduleId } },
      }),
    ),

  updateLesson: (courseId: string, lessonId: string, body: UpdateLessonBody) =>
    unwrap(
      api.PATCH('/v1/catalog/courses/{course_id}/lessons/{lesson_id}', {
        params: { path: { course_id: courseId, lesson_id: lessonId } },
        body,
      }),
    ),

  deleteLesson: (courseId: string, lessonId: string) =>
    unwrap(
      api.DELETE('/v1/catalog/courses/{course_id}/lessons/{lesson_id}', {
        params: { path: { course_id: courseId, lesson_id: lessonId } },
      }),
    ),

  addModule: (courseId: string, title: string) =>
    unwrap(api.POST('/v1/catalog/courses/{course_id}/modules', { ...course(courseId), body: { title } })),

  addLesson: (courseId: string, moduleId: string, body: AddLessonBody) =>
    unwrap(
      api.POST('/v1/catalog/courses/{course_id}/modules/{module_id}/lessons', {
        params: { path: { course_id: courseId, module_id: moduleId } },
        body,
      }),
    ),

  attachVideo: (courseId: string, lessonId: string, assetId: string) =>
    unwrap(
      api.PUT('/v1/catalog/courses/{course_id}/lessons/{lesson_id}/video', {
        params: { path: { course_id: courseId, lesson_id: lessonId } },
        body: { asset_id: assetId },
      }),
    ),

  reorderLessons: (courseId: string, moduleId: string, orderedLessonIds: string[]) =>
    unwrap(
      api.PUT('/v1/catalog/courses/{course_id}/modules/{module_id}/lessons/order', {
        params: { path: { course_id: courseId, module_id: moduleId } },
        body: { ordered_lesson_ids: orderedLessonIds },
      }),
    ),

  publish: (courseId: string) =>
    unwrap(api.POST('/v1/catalog/courses/{course_id}/publish', course(courseId))),

  archive: (courseId: string) =>
    unwrap(api.POST('/v1/catalog/courses/{course_id}/archive', course(courseId))),

  // ── Materiais da aula ──

  /** Todos os materiais do curso, de uma vez — cada um com a sua aula. */
  courseMaterials: (courseId: string) =>
    unwrap(api.GET('/v1/catalog/courses/{course_id}/materials', course(courseId))),

  requestMaterialUpload: (
    courseId: string,
    lessonId: string,
    body: { content_type: string; size_bytes: number },
  ) =>
    unwrap(
      api.POST('/v1/catalog/courses/{course_id}/lessons/{lesson_id}/materials/uploads', {
        params: { path: { course_id: courseId, lesson_id: lessonId } },
        body,
      }),
    ),

  addMaterialFile: (
    courseId: string,
    lessonId: string,
    body: { title: string; key: string; filename: string; content_type: string },
  ) =>
    unwrap(
      api.POST('/v1/catalog/courses/{course_id}/lessons/{lesson_id}/materials/files', {
        params: { path: { course_id: courseId, lesson_id: lessonId } },
        body,
      }),
    ),

  addMaterialLink: (courseId: string, lessonId: string, body: { title: string; url: string }) =>
    unwrap(
      api.POST('/v1/catalog/courses/{course_id}/lessons/{lesson_id}/materials/links', {
        params: { path: { course_id: courseId, lesson_id: lessonId } },
        body,
      }),
    ),

  renameMaterial: (courseId: string, lessonId: string, materialId: string, title: string) =>
    unwrap(
      api.PATCH('/v1/catalog/courses/{course_id}/lessons/{lesson_id}/materials/{material_id}', {
        params: { path: { course_id: courseId, lesson_id: lessonId, material_id: materialId } },
        body: { title },
      }),
    ),

  reorderMaterials: (courseId: string, lessonId: string, orderedIds: string[]) =>
    unwrap(
      api.PUT('/v1/catalog/courses/{course_id}/lessons/{lesson_id}/materials/order', {
        params: { path: { course_id: courseId, lesson_id: lessonId } },
        body: { ordered_material_ids: orderedIds },
      }),
    ),

  deleteMaterial: (courseId: string, lessonId: string, materialId: string) =>
    unwrap(
      api.DELETE('/v1/catalog/courses/{course_id}/lessons/{lesson_id}/materials/{material_id}', {
        params: { path: { course_id: courseId, lesson_id: lessonId, material_id: materialId } },
      }),
    ),

  /**
   * PUT do arquivo direto no storage, com a URL pré-assinada: o arquivo
   * nunca passa pela API. XHR e não fetch porque só ele reporta progresso.
   */
  putMaterial: (
    ticket: Pick<MaterialTicket, 'method' | 'upload_url' | 'headers'>,
    file: Blob,
    onProgress: (ratio: number) => void,
  ) =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(ticket.method, ticket.upload_url, true);
      for (const [k, v] of Object.entries(ticket.headers)) xhr.setRequestHeader(k, v);
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new ApiError(xhr.status, 'storage_error', 'O armazenamento recusou o arquivo.'));
      xhr.onerror = () => reject(new ApiError(0, 'network_error', 'Falha de rede durante o envio.'));
      xhr.send(file);
    }),
};
